'use client';

import React, { useEffect, useState } from 'react';

import { useRouter } from 'next/navigation';

import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { useTenantCurrency } from '@kit/ui/currency-icon';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@kit/ui/dialog';
import { toast } from '@kit/ui/sonner';

import { HyperpayCheckoutModal } from '../../../../../../packages/billing/hyperpay/src/components/hyperpay-checkout-modal';
import { useBookingPresentation } from './booking-presentation';

interface HyperpayCheckoutDialogProps {
  isOpen: boolean;
  onClose: () => void;
  checkoutData: {
    sessionId: string;
    integrity?: string;
    provider: string;
  } | null;
  /**
   * Base path to return to after a frictionless (non-3DS) success. Defaults to
   * /book/return (booking flow). The /payment flow passes /payment/[bookingId]
   * so HyperPay reconciles on the same page as Stripe/Moyasar/Ziina. For 3DS the
   * server-set shopperResultUrl decides the target — this governs only the
   * inline-callback path.
   */
  returnPath?: string;
  onSuccess?: () => void;
  /**
   * Overrides the tenant currency (ISO 4217) used to build the Apple Pay sheet
   * config. Normally omitted — the currency is read from TenantCurrencyProvider,
   * mounted in (tenant)/layout.tsx above every call site of this dialog.
   */
  currency?: string;
  /** Merchant name shown on the Apple Pay sheet (falls back to the hostname). */
  displayName?: string;
}

export function HyperpayCheckoutDialog({
  isOpen,
  onClose,
  checkoutData,
  returnPath = '/book/return',
  onSuccess,
  currency,
  displayName,
}: HyperpayCheckoutDialogProps) {
  const presentation = useBookingPresentation();
  const router = useRouter();
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);
  // Tenant currency drives the Apple Pay sheet's supportedCountries /
  // supportedNetworks. Read from context so all ten call sites pick it up
  // without threading a prop; an explicit prop still wins.
  const tenantCurrency = useTenantCurrency();

  // Handle payment success
  const handlePaymentSuccess = async (data: any) => {
    setIsProcessing(true);

    const sep = returnPath.includes('?') ? '&' : '?';

    try {
      // Check if we have a resource path from the payment
      if (data.resourcePath) {
        // Return to the configured page with the resource path so it reconciles.
        router.push(
          `${returnPath}${sep}provider=hyperpay&resourcePath=${encodeURIComponent(data.resourcePath)}`,
        );
      } else {
        toast.success(t('bookings:toastPaymentSuccessRedirecting'));
        if (onSuccess) {
          onSuccess();
        }
        router.push(`${returnPath}${sep}provider=hyperpay&status=success`);
      }
    } catch (error) {
      console.error('Error handling payment success:', error);
      toast.error(t('bookings:toastPaymentProcessedError'));
    }
  };

  // Handle payment error
  const handlePaymentError = (error: any) => {
    console.error('Payment error:', error);
    toast.error(t('bookings:toastPaymentRetry'));
    setIsProcessing(false);
  };

  // Handle dialog close
  const handleClose = () => {
    if (!isProcessing) {
      onClose();
    }
  };

  if (!checkoutData || checkoutData.provider !== 'hyperpay') {
    return null;
  }

  return (
    <HyperpayCheckoutModal
      contentClassName={presentation.className}
      contentStyle={presentation.style}
      isOpen={isOpen}
      onClose={handleClose}
      checkoutId={checkoutData.sessionId}
      integrity={checkoutData.integrity}
      baseUrl={
        process.env.NEXT_PUBLIC_HYPERPAY_BASE_URL || 'https://eu-test.oppwa.com'
      }
      onSuccess={handlePaymentSuccess}
      onError={handlePaymentError}
      currency={currency ?? tenantCurrency}
      displayName={displayName}
    />
  );
}
